import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";

import {
  getAgentSummary,
  downloadAgentsReportPDF,
  downloadDSAOnboardedCustomersCSV,
} from "../../api/agents";
import { SearchFilterContainer } from "../../components/SearchFilterTable";
import { OutlineButton, PrimaryButton } from "../../components/Button";
import useMessageModal from "../../hooks/useMessageModal.hook";
import { createCSV } from "../../utils/helpers";

function safeLower(v) {
  return String(v ?? "").toLowerCase();
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function AgentPerformance() {
  const [setPageTitle] = useOutletContext();
  setPageTitle("Agent Performance");

  const navigate = useNavigate();
  const showMessageModal = useMessageModal();

  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);

  const toastError = (error, fallback) => {
    showMessageModal({
      title: "Error",
      message: error?.response?.data?.errorMessage || error?.message || fallback,
      isError: true,
      closeButtonText: "Close",
    });
  };

  async function load() {
    setLoading(true);
    setLoadError("");
    try {
      const res = await getAgentSummary({ days });
      setData(res);
    } catch (e) {
      console.error(e);
      setLoadError(e?.response?.data?.errorMessage || e?.message || "Failed to load agent summary.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const filteredAgents = useMemo(() => {
    const agents = Array.isArray(data?.agents) ? data.agents : [];
    const q = safeLower(search).trim();
    if (!q) return agents;

    return agents.filter((a) => {
      const name = safeLower(a?.fullName);
      const email = safeLower(a?.email);
      const id = safeLower(a?.agentId);
      return name.includes(q) || email.includes(q) || id.includes(q);
    });
  }, [data?.agents, search]);

  const reloadTable = ({ search: s }) => setSearch(s || "");

  const prependWidget = (
    <div className="flex center gap-12">
      <select value={days} onChange={(e) => setDays(Number(e.target.value))}>
        {[7, 30, 60, 90].map((n) => (
          <option key={n} value={n}>
            Last {n} days
          </option>
        ))}
      </select>

      <OutlineButton className="mini" onClick={load} disabled={loading}>
        {loading ? "Loading..." : "Refresh"}
      </OutlineButton>

      <PrimaryButton
        className="mini"
        disabled={downloading || loading}
        onClick={async () => {
          setDownloading(true);
          try {
            const blob = await downloadAgentsReportPDF({ days });
            downloadBlob(blob, `agents_report_last_${days}_days.pdf`);
          } catch (e) {
            console.error(e);
            toastError(e, "Failed to download PDF report.");
          } finally {
            setDownloading(false);
          }
        }}
      >
        {downloading ? "Preparing..." : "Download PDF"}
      </PrimaryButton>

      <PrimaryButton
        className="mini"
        disabled={downloadingCSV || loading}
        onClick={async () => {
          setDownloadingCSV(true);
          try {
            const csvText = await downloadDSAOnboardedCustomersCSV({ days });
            createCSV(
              csvText,
              `dsa_onboarded_customers_report_last_${days}_days`
            );
          } catch (e) {
            console.error(e);
            toastError(e, "Failed to download DSA onboarded customers report.");
          } finally {
            setDownloadingCSV(false);
          }
        }}
      >
        {downloadingCSV ? "Preparing..." : "DSA Onboarded Customers Report"}
      </PrimaryButton>
    </div>
  );

  if (loadError) {
    return <div className="flex center box fs-20">{loadError}</div>;
  }

  return (
    <div className="agents">
      <div className="box mb-32">
        <div className="flex between-center mb-16">
          <h3 className="fs-20">Agent Performance</h3>
          <span className="fs-14 charcoal-80">
            Track productivity (accounts + loans) and export reports.
          </span>
        </div>

        <div className="flex gap-12 wrap">
          <div className="box mini">
            <div className="fs-14 charcoal-60">Total Agents</div>
            <div className="fs-20">{data?.totalAgents ?? 0}</div>
          </div>
          <div className="box mini">
            <div className="fs-14 charcoal-60">Draft Accounts</div>
            <div className="fs-20">{data?.totalDraftAccounts ?? 0}</div>
          </div>
          <div className="box mini">
            <div className="fs-14 charcoal-60">Submitted Requests</div>
            <div className="fs-20">{data?.totalSubmittedAccountRequests ?? 0}</div>
          </div>
          <div className="box mini">
            <div className="fs-14 charcoal-60">Approved Accounts</div>
            <div className="fs-20">{data?.totalApprovedCustomerAccounts ?? 0}</div>
          </div>
          <div className="box mini">
            <div className="fs-14 charcoal-60">Loans Booked</div>
            <div className="fs-20">{data?.totalLoansBooked ?? 0}</div>
          </div>
          <div className="box mini">
            <div className="fs-14 charcoal-60">Loans Approved</div>
            <div className="fs-20">{data?.totalLoansApproved ?? 0}</div>
          </div>
        </div>
      </div>

      <SearchFilterContainer
        searchHint="Search agent (name, email, id)"
        reloadTable={reloadTable}
        prependWidget={prependWidget}
      >
        <table className="box fs-20 mini">
          <thead>
            <tr>
              <th>Agent</th>
              <th>Email</th>
              <th>Draft</th>
              <th>Submitted</th>
              <th>Approved</th>
              <th>Loans Booked</th>
              <th>Loans Approved</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="center-text">
                  Loading...
                </td>
              </tr>
            ) : filteredAgents.length === 0 ? (
              <tr>
                <td colSpan={8} className="center-text">
                  No agents found.
                </td>
              </tr>
            ) : (
              filteredAgents.map((a) => (
                <tr key={a?.agentId}>
                  <td>
                    <div className="fs-16">{a?.fullName || "-"}</div>
                    <div className="fs-14 charcoal-60">{a?.agentId}</div>
                  </td>
                  <td className="fs-16">{a?.email || "-"}</td>
                  <td className="fs-16">{a?.draftAccounts ?? 0}</td>
                  <td className="fs-16">{a?.submittedAccounts ?? 0}</td>
                  <td className="fs-16">{a?.approvedAccounts ?? 0}</td>
                  <td className="fs-16">{a?.loansBooked ?? 0}</td>
                  <td className="fs-16">{a?.loansApproved ?? 0}</td>
                  <td>
                    <button
                      className="btn outline mini"
                      onClick={() => navigate(`/agents/${a?.agentId}/activities`)}
                    >
                      View Activities
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </SearchFilterContainer>
    </div>
  );
}
